import 'react-calendar/dist/Calendar.css';
import Layout from '../../components/layoutConfig/layout';
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { setupAPIclient } from '../../services/setupAPIclient';
import { FiMail, FiSmartphone, FiUser } from 'react-icons/fi';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import 'animate.css';
import LocalStorage from '../../helpers/localstorage';
import GuidedTutorial from "../../components/common/GuidedTutorial";

interface Permissions {
  sms: boolean;
  email: boolean;
}

function NotificationPage() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  const [permissions, setPermissions] = useState<Permissions>({
    sms: false,
    email: false,
  });
  const [notificationId, setNotificationId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const smsRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);
  const saveBtnWrapRef = useRef<HTMLDivElement>(null);
  const saveBtnRef = useRef<HTMLButtonElement>(null);
  const isNotificationTutorialActive = localStorage.getStep() === "6";

  // Buscar notificação existente
  const handleGetNotification = async () => {
    try {
      setLoading(true);
      const response = await ApiClient.post("/management/get/notification");

      if (response?.data?.success?.length > 0) {
        const data = response.data.success[0];
        setNotificationId(data.id);
        setPermissions({
          sms: data.sms,
          email: data.email,
        });
      } else {
        setNotificationId(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar configurações de notificação!");
    } finally {
      setLoading(false);
    }
  };

  // Criar nova notificação
  const handleCreateNotification = async () => {
    try {
      setSaving(true);
      const payload = {
        mensage: "",
        sms: permissions.sms,
        email: permissions.email,
        a_message: false
        };

      const response = await ApiClient.post("/management/create/notification", payload);
      if (response.data) {
        toast.success("Configurações criadas com sucesso!");
        handleGetNotification(); // Atualiza dados na tela
        window.dispatchEvent(new Event("management:onboarding-updated"));
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao criar configurações!");
    } finally {
      setSaving(false);
    }
  };

  // Atualizar notificação existente
  const handleUpdateNotification = async () => {
    if (!notificationId) return;
    try {
      setSaving(true);
      const payload = {
        id: notificationId,
        mensage: "",
        sms: permissions.sms,
        email: permissions.email,
        a_message: false
      };

      const response = await ApiClient.post("/management/update/notification", payload);
      if (response.data) {
        toast.success("Configurações atualizadas com sucesso!");
        handleGetNotification();
        window.dispatchEvent(new Event("management:onboarding-updated"));
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao atualizar configurações!");
    } finally {
      setSaving(false);
    }
  };

  // Lógica do botão principal
  const handleSaveNotification = () => {
    if (notificationId) {
      handleUpdateNotification();
    } else {
      handleCreateNotification();
    }
  };

  // Alternar switches
  const handleToggle = (type: keyof Permissions) => {
    setPermissions(prev => ({ ...prev, [type]: !prev[type] }));
  };

  useEffect(() => {
    handleGetNotification();
  }, []);

  return (
    <Layout>
      <GuidedTutorial
        storageKey="management_tutorial_notification_v3"
        enabled={isNotificationTutorialActive && !loading}
        lockInteractionToTarget
        clearReplayOnFinish
        markTutorialCompleted
        steps={[
          {
            targetRef: smsRef as any,
            title: "",
            description: "Ative SMS para receber lembretes de vencimento no celular.",
          },
          {
            targetRef: emailRef as any,
            title: "",
            description: "Ative E-mail para receber alertas e avisos importantes.",
          },
          {
            targetRef: saveBtnRef as any,
            title: "",
            description: "Clique em salvar para aplicar as permissões de notificação.",
            elevateParentSelectors: [".notification-save-wrap", ".notification-header-row"],
          },
        ]}
      />
      <div className="container py-2">
       <div
  className="mb-5 animate__animated animate__fadeInDown d-flex align-items-center justify-content-between notification-header-row"
>
  <h2
    className="text-left m-0"
    style={{ color: "#5dade2" }}
  >
    Configuração de Notificações
  </h2>

  <div ref={saveBtnWrapRef} className="notification-save-wrap">
    <button
      ref={saveBtnRef}
      onClick={handleSaveNotification}
      disabled={saving || (loading && !isNotificationTutorialActive)}
      type="button"
      style={{ background: "#5dade2", color: "white" }}
      className="btn btn-lg animate__animated animate__pulse px-5 notification-save-btn"
    >
      {saving
        ? "Salvando..."
        : notificationId
        ? "Atualizar Configurações"
        : "Salvar Configurações"}
    </button>
  </div>
</div>


       

        <hr />

        {loading ? (
          <div className="text-center py-2">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3">Carregando configurações...</p>
          </div>
        ) : (
          <div className="row g-4">
          

            {/* SMS */}
            <div className="col-12 col-md-6">
              <div className="card notification-card shadow-sm p-4 h-100 animate__animated animate__fadeInUp hover-shadow bg-light">
                <FiSmartphone size={50} className="text-success mb-3 hover-icon" title="Notificações por SMS" />
                <h5 className="fw-semibold">SMS</h5>
                <p className="text-muted small">
                  Ative para receber alertas importantes diretamente no seu celular.
                </p>
                <div className="form-check form-switch mt-2">
                  <div ref={smsRef} className="notification-switch-row">
                    <input
                      className="form-check-input notification-switch"
                      type="checkbox"
                      id="smsSwitch"
                      checked={permissions.sms}
                      onChange={() => handleToggle("sms")}
                    />
                    <label className="form-check-label" htmlFor="smsSwitch">
                      Ativar SMS
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="col-12 col-md-6">
              <div className="card notification-card shadow-sm p-4 h-100 animate__animated animate__fadeInRight hover-shadow bg-light">
                <FiMail size={50} className="text-info mb-3 hover-icon" title="Notificações por Email" />
                <h5 className="fw-semibold">Email</h5>
                <p className="text-muted small">
                  Ative para receber relatórios e avisos importantes diretamente no seu email.
                </p>
                <div className="form-check form-switch mt-2">
                  <div ref={emailRef} className="notification-switch-row">
                    <input
                      className="form-check-input notification-switch"
                      type="checkbox"
                      id="emailSwitch"
                      checked={permissions.email}
                      onChange={() => handleToggle("email")}
                    />
                    <label className="form-check-label" htmlFor="emailSwitch">
                      Ativar Email
                    </label>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}


      </div>

      {/* Estilo adicional */}
      <style jsx>{`
        .hover-shadow:hover {
          transform: translateY(-5px);
          transition: all 0.3s ease;
          box-shadow: 0 15px 25px rgba(0, 0, 0, 0.2);
        }
        .hover-icon:hover {
          transform: scale(1.3);
          transition: all 0.3s ease;
        }
        .notification-card.guided-highlight-parent {
          position: relative;
          z-index: 1079;
        }
        .notification-header-row.guided-highlight-parent {
          position: relative;
          z-index: 1079;
        }
        .notification-card.guided-highlight-parent::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: rgba(15, 23, 42, 0.36);
          pointer-events: none;
        }
        .notification-switch {
          cursor: pointer;
        }
        .notification-switch-row {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.35rem 0.6rem;
          border-radius: 999px;
          position: relative;
          z-index: 1081;
        }
        .notification-switch-row .form-check-label {
          margin-bottom: 0;
          font-weight: 600;
          color: #475569;
        }
        .notification-switch-row.guided-highlight-target {
          background: #ffffff !important;
          border: 1px solid rgba(93, 173, 226, 0.38);
          opacity: 1 !important;
          filter: none !important;
          box-shadow:
            0 0 0 3px rgba(93, 173, 226, 0.95),
            0 0 0 7px rgba(93, 173, 226, 0.22),
            0 16px 36px rgba(15, 23, 42, 0.22) !important;
        }
        .notification-switch-row.guided-highlight-target,
        .notification-save-btn.guided-highlight-target {
          isolation: isolate;
        }
        .notification-card.guided-highlight-parent .notification-switch-row.guided-highlight-target {
          z-index: 1082;
        }
        .notification-switch-row.guided-highlight-target * {
          opacity: 1 !important;
          filter: none !important;
        }
        .notification-switch-row.guided-highlight-target .form-check-label {
          color: #0f172a !important;
        }
        .notification-switch-row.guided-highlight-target .notification-switch {
          margin-top: 0;
          flex-shrink: 0;
          background-color: #ffffff !important;
          border-color: #bfdbfe !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%235dade2'/%3e%3c/svg%3e") !important;
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.98),
            0 0 0 1px rgba(191, 219, 254, 0.9) !important;
        }
        .notification-switch-row.guided-highlight-target .notification-switch:checked {
          background-color: #5dade2 !important;
          border-color: #5dade2 !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23ffffff'/%3e%3c/svg%3e") !important;
        }
        .notification-switch-row.guided-highlight-target .notification-switch:focus {
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.7),
            0 0 0 0.2rem rgba(93, 173, 226, 0.18) !important;
        }
        .notification-save-wrap {
          position: relative;
          z-index: 1;
          display: inline-flex;
        }
        .notification-save-wrap.guided-highlight-parent {
          position: relative;
          z-index: 1081 !important;
        }
        .notification-save-btn {
          position: relative;
          z-index: 1;
        }
        .notification-save-btn.guided-highlight-target {
          background: #5dade2 !important;
          color: #ffffff !important;
          border-color: #5dade2 !important;
          opacity: 1 !important;
          filter: none !important;
          font-weight: 700;
          pointer-events: auto !important;
          cursor: pointer !important;
          z-index: 1082 !important;
          box-shadow:
            0 0 0 3px rgba(93, 173, 226, 0.95),
            0 0 0 7px rgba(93, 173, 226, 0.22),
            0 10px 22px rgba(15, 23, 42, 0.12) !important;
        }
        .notification-save-btn.guided-highlight-target:hover,
        .notification-save-btn.guided-highlight-target:focus,
        .notification-save-btn.guided-highlight-target:active {
          background: #5dade2 !important;
          color: #ffffff !important;
          border-color: #5dade2 !important;
        }
        [data-theme="dark"] .notification-switch {
          background-color: #475569 !important;
          border-color: #64748b !important;
          background-image: var(--bs-form-switch-bg) !important;
        }
        [data-theme="dark"] .notification-card.guided-highlight-parent::after {
          background: rgba(2, 6, 23, 0.44);
        }
        [data-theme="dark"] .notification-switch-row .form-check-label {
          color: #cbd5e1;
        }
        [data-theme="dark"] .notification-switch-row.guided-highlight-target {
          background: #ffffff !important;
          border-color: rgba(93, 173, 226, 0.38);
        }
        [data-theme="dark"] .notification-switch-row.guided-highlight-target .form-check-label {
          color: #0f172a !important;
        }
        [data-theme="dark"] .notification-switch-row.guided-highlight-target .notification-switch {
          background-color: #ffffff !important;
          border-color: #e5e7eb !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%235dade2'/%3e%3c/svg%3e") !important;
        }
        [data-theme="dark"] .notification-switch-row.guided-highlight-target .notification-switch:checked {
          background-color: #5dade2 !important;
          border-color: #5dade2 !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23ffffff'/%3e%3c/svg%3e") !important;
        }
        [data-theme="dark"] .notification-save-btn.guided-highlight-target {
          background: #5dade2 !important;
          color: #ffffff !important;
          border-color: #5dade2 !important;
        }
        [data-theme="dark"] .notification-switch:checked {
          background-color: #5dade2 !important;
          border-color: #5dade2 !important;
          background-image: var(--bs-form-switch-bg) !important;
        }
        [data-theme="dark"] .notification-switch:focus {
          box-shadow: 0 0 0 0.2rem rgba(93, 173, 226, 0.25) !important;
        }
        textarea.form-control {
          resize: none;
        }
      `}</style>
    </Layout>
  );
}

export default NotificationPage;
